import type {
  AuditQuery,
  CreateUserInput,
  DepartmentInput,
  DocumentTypeInput,
  OrganizationSettings,
  Permission,
  SystemRole,
} from '@qualicloud/contracts';
import { ApiScope, request, requestFile } from './client';
import type {
  AuditPage,
  ChainStatus,
  ChainVerificationReport,
  DepartmentDetail,
  DepartmentNode,
  DocumentTypeNode,
  NotificationInbox,
  NotificationPreferences,
  OrganizationView,
  PagedUsers,
  UserDetail,
  VendorAccessElevation,
  VendorAccessGrant,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de documento — CORE
// ─────────────────────────────────────────────────────────────────────────────

export const documentTypesApi = {
  /** Árvore hierárquica por ID (corrige C14: o legado casava por NOME). */
  tree(): Promise<DocumentTypeNode[]> {
    return request<DocumentTypeNode[]>({ scope: ApiScope.CORE, path: '/document-types' });
  },

  detail(id: string): Promise<DocumentTypeNode> {
    return request<DocumentTypeNode>({ scope: ApiScope.CORE, path: `/document-types/${id}` });
  },

  create(input: DocumentTypeInput): Promise<{ id: string }> {
    return request<{ id: string }>({
      scope: ApiScope.CORE,
      path: '/document-types',
      method: 'POST',
      body: input,
    });
  },

  update(id: string, input: DocumentTypeInput): Promise<DocumentTypeNode> {
    return request<DocumentTypeNode>({
      scope: ApiScope.CORE,
      path: `/document-types/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /**
   * Máscara de numeração do tipo (`{TIPO}`, `{SEQ:n}`).
   *
   * PENDENTE DE GATEWAY: o CORE implementa `PUT /document-types/:id/numbering`,
   * mas a rota ainda não está na lista branca do gateway
   * (`ms-gateway/src/modules/proxy/route-table.ts`). Enquanto não estiver,
   * a chamada devolve 404 — por isso não há tela que a acione: oferecer um
   * campo que sempre falha é pior do que não oferecer.
   */
  configureNumbering(
    id: string,
    input: { enabled: boolean; mask?: string | null; prefix?: string | null },
  ): Promise<DocumentTypeNode> {
    return request<DocumentTypeNode>({
      scope: ApiScope.CORE,
      path: `/document-types/${id}/numbering`,
      method: 'PUT',
      body: input,
    });
  },

  async remove(id: string): Promise<void> {
    await request({ scope: ApiScope.CORE, path: `/document-types/${id}`, method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Usuários — IDENTITY
// ─────────────────────────────────────────────────────────────────────────────

export const usersApi = {
  list(filter: {
    page?: number;
    pageSize?: number;
    search?: string;
    active?: boolean;
    departmentId?: string;
  }): Promise<PagedUsers> {
    return request<PagedUsers>({
      scope: ApiScope.IDENTITY,
      path: '/users',
      query: filter as Record<string, unknown>,
    });
  },

  detail(userId: string): Promise<UserDetail> {
    return request<UserDetail>({ scope: ApiScope.IDENTITY, path: `/users/${userId}` });
  },

  create(input: CreateUserInput & { initialPassword: string }): Promise<{ id: string; username: string }> {
    return request<{ id: string; username: string }>({
      scope: ApiScope.IDENTITY,
      path: '/users',
      method: 'POST',
      body: input,
    });
  },

  /** `username` NÃO é atualizável — §11.300(a) exige unicidade permanente do ID. */
  update(
    userId: string,
    input: {
      email?: string;
      firstName?: string;
      lastName?: string;
      jobTitle?: string;
      active?: boolean;
      departmentIds?: string[];
      reason?: string;
    },
  ): Promise<UserDetail> {
    return request<UserDetail>({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}`,
      method: 'PATCH',
      body: input,
    });
  },

  changeRole(userId: string, role: SystemRole, reason: string): Promise<{ role: string; changed: boolean }> {
    return request<{ role: string; changed: boolean }>({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}/role`,
      method: 'POST',
      body: { role, reason },
    });
  },

  setPermissionOverride(
    userId: string,
    permission: Permission,
    effect: 'ALLOW' | 'DENY',
    reason: string,
  ): Promise<{ permission: string; effect: string }> {
    return request<{ permission: string; effect: string }>({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}/permissions`,
      method: 'POST',
      body: { permission, effect, reason },
    });
  },

  clearPermissionOverride(userId: string, permission: Permission, reason: string) {
    // O valor da permission contém `:` — precisa ir codificado no caminho.
    return request<{ permission: string; cleared: true }>({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}/permissions/${encodeURIComponent(permission)}`,
      method: 'DELETE',
      query: { reason },
    });
  },

  async resetPassword(userId: string, newPassword: string, reason: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}/password-reset`,
      method: 'POST',
      body: { newPassword, reason },
    });
  },

  async unlock(userId: string, reason: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/users/${userId}/unlock`,
      method: 'POST',
      query: { reason },
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Departamentos — IDENTITY
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Diretório para SELEÇÃO — nome, cargo e lotação, numa consulta só.
 *
 * Não exige `user:manage`: `GET /users` é administração de conta e continua
 * protegida. Esta rota devolve estritamente o que um seletor precisa, e é o que
 * permite o Gestor da Qualidade atribuir ação corretiva, convidar para reunião
 * e endereçar circular.
 */
export interface DirectoryPerson {
  readonly id: string;
  readonly name: string;
  readonly jobTitle: string;
  readonly active: boolean;
  readonly departmentIds: readonly string[];
}

export interface DirectoryView {
  readonly people: readonly DirectoryPerson[];
  readonly departments: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

export const directoryApi = {
  find(): Promise<DirectoryView> {
    return request<DirectoryView>({ scope: ApiScope.IDENTITY, path: '/directory', method: 'GET' });
  },
};

export const departmentsApi = {
  async tree(): Promise<DepartmentNode[]> {
    const response = await request<{ departments: DepartmentNode[] }>({
      scope: ApiScope.IDENTITY,
      path: '/departments',
    });
    return response.departments;
  },

  detail(departmentId: string): Promise<DepartmentDetail> {
    return request<DepartmentDetail>({
      scope: ApiScope.IDENTITY,
      path: `/departments/${departmentId}`,
    });
  },

  create(input: DepartmentInput): Promise<{ id: string; name: string }> {
    return request<{ id: string; name: string }>({
      scope: ApiScope.IDENTITY,
      path: '/departments',
      method: 'POST',
      body: input,
    });
  },

  update(
    departmentId: string,
    input: Partial<DepartmentInput> & { active?: boolean; reason?: string },
  ) {
    return request<{
      id: string;
      name: string;
      description: string;
      parentId: string | null;
      active: boolean;
    }>({
      scope: ApiScope.IDENTITY,
      path: `/departments/${departmentId}`,
      method: 'PATCH',
      body: input,
    });
  },

  async remove(departmentId: string, reason: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/departments/${departmentId}`,
      method: 'DELETE',
      query: { reason },
    });
  },

  async addMember(departmentId: string, userId: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/departments/${departmentId}/members`,
      method: 'POST',
      body: { userId },
    });
  },

  async removeMember(departmentId: string, userId: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/departments/${departmentId}/members/${userId}`,
      method: 'DELETE',
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Organização e acesso de fornecedor — IDENTITY
// ─────────────────────────────────────────────────────────────────────────────

export const organizationsApi = {
  detail(organizationId: string): Promise<OrganizationView> {
    return request<OrganizationView>({
      scope: ApiScope.IDENTITY,
      path: `/organizations/${organizationId}`,
    });
  },

  /** `slug` é imutável — o backend o omite do schema de atualização. */
  update(
    organizationId: string,
    input: Partial<Omit<OrganizationSettings, 'slug'>>,
  ): Promise<OrganizationView> {
    return request<OrganizationView>({
      scope: ApiScope.IDENTITY,
      path: `/organizations/${organizationId}`,
      method: 'PATCH',
      body: input,
    });
  },
};

export const vendorAccessApi = {
  async list(): Promise<VendorAccessGrant[]> {
    const response = await request<{ grants: VendorAccessGrant[] }>({
      scope: ApiScope.IDENTITY,
      path: '/vendor-access',
    });
    return response.grants;
  },

  /**
   * Elevação de acesso do FORNECEDOR à organização de um cliente.
   * Sempre gera evento `VENDOR_ACCESS` na trilha (plano §6.5) e exige
   * justificativa de no mínimo 20 caracteres.
   */
  elevate(input: {
    organizationId: string;
    justification: string;
    minutes?: number;
  }): Promise<VendorAccessElevation> {
    return request<VendorAccessElevation>({
      scope: ApiScope.IDENTITY,
      path: '/vendor-access/elevate',
      method: 'POST',
      body: input,
    });
  },

  async revoke(grantId: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: `/vendor-access/${grantId}`,
      method: 'DELETE',
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Trilha de auditoria — via gateway (o AUDIT não é acessível pelo browser)
// ─────────────────────────────────────────────────────────────────────────────

export const auditApi = {
  events(query: Partial<AuditQuery>): Promise<AuditPage> {
    return request<AuditPage>({
      scope: ApiScope.AUDIT,
      path: '/events',
      query: query as Record<string, unknown>,
    });
  },

  export(
    query: Partial<AuditQuery>,
    format: 'csv' | 'json',
  ): Promise<{ blob: Blob; fileName: string }> {
    return requestFile({
      scope: ApiScope.AUDIT,
      path: '/events/export',
      query: { ...query, format },
      fallbackFileName: `trilha-auditoria.${format}`,
    });
  },

  chainStatus(): Promise<ChainStatus> {
    return request<ChainStatus>({ scope: ApiScope.AUDIT, path: '/chain/status' });
  },

  verifyChain(): Promise<ChainVerificationReport> {
    return request<ChainVerificationReport>({
      scope: ApiScope.AUDIT,
      path: '/chain/verify',
      method: 'POST',
      body: {},
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Notificações
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Caixa in-app.
 *
 * Os caminhos são os DECLARADOS PELO GATEWAY (`/notifications`,
 * `/notifications/unread-count`, `/notifications/read-all`,
 * `/notifications/:id/read`) — o serviço NOTIFICATIONS nunca expôs `/inbox*`,
 * que era o prefixo usado aqui antes e não casava com rota nenhuma.
 */
export const notificationsApi = {
  inbox(params: { page: number; pageSize: number; onlyUnread: boolean }): Promise<NotificationInbox> {
    return request<NotificationInbox>({
      scope: ApiScope.NOTIFICATIONS,
      path: '',
      query: params as unknown as Record<string, unknown>,
    });
  },

  unreadCount(): Promise<{ unreadCount: number }> {
    return request<{ unreadCount: number }>({
      scope: ApiScope.NOTIFICATIONS,
      path: '/unread-count',
    });
  },

  /**
   * O serviço marca UMA notificação por vez (`POST /notifications/:id/read`),
   * porque cada leitura gera sua própria linha de transição DELIVERED → READ —
   * evidência de entrega, não um contador. A tela continua pedindo em lote; o
   * lote é desdobrado aqui.
   */
  async markRead(notificationIds: readonly string[]): Promise<void> {
    await Promise.all(
      notificationIds.map((id) =>
        request({
          scope: ApiScope.NOTIFICATIONS,
          path: `/${encodeURIComponent(id)}/read`,
          method: 'POST',
          body: {},
        }),
      ),
    );
  },

  async markAllRead(): Promise<void> {
    await request({
      scope: ApiScope.NOTIFICATIONS,
      path: '/read-all',
      method: 'POST',
      body: {},
    });
  },

  preferences(): Promise<NotificationPreferences> {
    return request<NotificationPreferences>({
      scope: ApiScope.NOTIFICATIONS,
      path: '/preferences',
    });
  },

  updatePreferences(input: NotificationPreferences): Promise<NotificationPreferences> {
    return request<NotificationPreferences>({
      scope: ApiScope.NOTIFICATIONS,
      path: '/preferences',
      method: 'PUT',
      body: input,
    });
  },
};
